/**
 * CleanPro Ops — Mobile (Capacitor) bootstrap
 *
 * This module is the SINGLE entry point for everything mobile-specific.
 * It runs on app boot, detects whether we're inside the native Android
 * shell, and wires the native plugins (push, biometric, camera, status
 * bar, share, network) into the existing React app.
 *
 * IMPORTANT — Single Source of Truth Guarantee
 * -------------------------------------------
 * No data is cached, persisted, or duplicated locally. Every API call
 * still goes through axios → process.env.REACT_APP_BACKEND_URL, which
 * is the SAME backend the web uses. The only thing stored on-device is
 * the auth token (mirrors localStorage behaviour on web).
 */
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Preferences } from '@capacitor/preferences';
import { PushNotifications } from '@capacitor/push-notifications';
import { Network } from '@capacitor/network';
import { toast } from 'sonner';

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform();

/**
 * Token persistence that works on both web (localStorage) and native
 * (Capacitor Preferences = encrypted KeyStore on Android).
 */
export const tokenStorage = {
  async get() {
    if (isNative()) {
      const { value } = await Preferences.get({ key: 'auth_token' });
      return value || null;
    }
    return localStorage.getItem('auth_token');
  },
  async set(token) {
    if (isNative()) {
      await Preferences.set({ key: 'auth_token', value: token });
      return;
    }
    localStorage.setItem('auth_token', token);
  },
  async remove() {
    if (isNative()) {
      await Preferences.remove({ key: 'auth_token' });
      return;
    }
    localStorage.removeItem('auth_token');
  },
};

/**
 * Register the device for push notifications and send the FCM token
 * to the backend so server-side events can target this user's phone.
 *
 * The backend endpoint POST /api/devices/register accepts:
 *   { token, platform: 'android', app_version }
 * and stores it on the user's profile. Tap actions are sent back via
 * the standard `notification` payload (handled in the listener below).
 */
async function registerPush(api) {
  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive !== 'granted') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') return;
    await PushNotifications.register();

    PushNotifications.addListener('registration', async (t) => {
      try {
        await api.post('/devices/register', {
          token: t.value,
          platform: platform(),
          app_version: '1.0.0',
        });
      } catch (_) {
        // Backend may not have this endpoint yet — non-fatal.
      }
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.warn('Push registration error', err);
    });

    PushNotifications.addListener(
      'pushNotificationReceived',
      (n) => {
        // Foreground display via sonner toast.
        toast(n.title || 'CleanPro Ops', {
          description: n.body || '',
        });
      },
    );

    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action) => {
        const data = action?.notification?.data || {};
        if (data.route) {
          window.location.assign(data.route);
        }
      },
    );
  } catch (err) {
    console.warn('registerPush failed', err);
  }
}

/**
 * Hardware back button → browser history back (matches expected Android UX).
 */
function wireHardwareBackButton() {
  CapApp.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      CapApp.exitApp();
    }
  });
}

/**
 * Track network status so users see a banner when offline. The app
 * remains read-only on the last-rendered screen until the connection
 * resumes — we never queue writes locally (single source of truth).
 */
function wireNetwork() {
  Network.addListener('networkStatusChange', (s) => {
    if (!s.connected) {
      toast.error('Χωρίς σύνδεση — απαιτείται για συγχρονισμό');
    }
  });
}

/**
 * Boot the mobile layer. Safe to call unconditionally; no-ops on web.
 */
export async function bootMobile(api) {
  if (!isNative()) return;
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#0a0a0a' });
  } catch (_) { /* not critical */ }

  wireHardwareBackButton();
  wireNetwork();
  await registerPush(api);

  // Hide splash once React has mounted enough to be visible.
  setTimeout(() => SplashScreen.hide().catch(() => {}), 400);
}
